import React, { ReactElement, useContext, useEffect, useState } from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import axios from "axios";
import Snackbar from "@material-ui/core/Snackbar";
import { UserContext } from "./contexts/UserContext";
import AccessDeniedPage from "./pages/Other/AccessDeniedPage";
import SearchPage from "./pages/Search/SearchPage";
import CustomVerticalNavbar from "./components/Navbar/VerticalNavbar";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import RequestsPage from "./pages/Requests/RequestsPage";
import ProductsPage from "./pages/Products/ProductsPage";
import BASE_PATH from "./routing";
import ErrorBoundary from "./components/Wrapper/ErrorBoundary";
import ErrorExamplePage from "./pages/Other/ErrorExamplePage";
import { GenericPermissionContextProvider, GenericPermissionContext } from "./contexts/GenericPermissionContext";
import { TestRequestPermissionContextProvider } from "./contexts/TestRequestPermissionContext";
import DeveloperMenu from "./helper/DeveloperMenu";
import VersionAlert from "./components/Alert/VersionAlert";
import { StatusFilterContextProvider } from "./contexts/StatusFilterContext";
import SurveySlideIn from "./components/Alert/SurveySlideIn";
import AdminPage from "./pages/Admin/AdminPage";
import ErrorSnackbarContext from "./contexts/ErrorSnackbarContext";
import { SelectedColumnsProvider } from "./contexts/SelectedColumnsContext";
import StageIndicator from "./helper/StageIndicator/StageIndicator";
import { SpecialContentContext } from "./contexts/SpecialContentContext";
import XmasSpecialDialog from "./components/Dialog/XmasSpecialDialog";
import LaboratoryPage from "./pages/Labs/LaboratoryPage";
import ReportTemplatePage from "./pages/Admin/ReportTemplates/ReportTemplatePage";

function App(): React.ReactElement {
  const { getUser } = useContext(UserContext);
  const { xmasSpecialDialogOpen, updateXmasSpecialDialogOpen } = useContext(SpecialContentContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [isVersionSnackbarOpen, setIsVersionSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const hasViewPermission = (roles?: string[]): boolean => {
    if (roles) {
      return (
        roles?.includes &&
        ["USER", "ADMIN", "REQUESTER", "STAKEHOLDER"] // mapped names, will be used in future
          .concat(["Users", "Admins", "LEAP_Requester", "LEAP_Stakeholder"]) // original names, will be removed
          .map((v) => roles.includes(v))
          .includes(true)
      );
    }
    return false;
  };

  const fetchEnvFile = () => {
    return axios.get(`./static/env/env.json?t=${new Date().getTime()}`);
  };

  const checkLocalVersion = () => {
    const localVersion = JSON.parse(localStorage.getItem("envObject") || "{}").deployment_version;

    fetchEnvFile().then((res) => {
      const envVersion = res.data?.deployment_version;

      if (localVersion !== envVersion) {
        setIsVersionSnackbarOpen(true);
      }
    });
  };

  useEffect(() => {
    getUser()
      .catch(handleHttpError)
      .finally(() => setLoading(false));
    setInterval(() => checkLocalVersion(), 300000);
  }, []);

  return (
    <UserContext.Consumer>
      {(UserContextProp): ReactElement => {
        const { error, user } = UserContextProp;
        return (
          <div>
            {/* use for auth testing on dev stage */}
            <XmasSpecialDialog open={xmasSpecialDialogOpen} onClose={updateXmasSpecialDialogOpen} />
            <DeveloperMenu />
            <StageIndicator />
            {hasViewPermission(user?.roles) && (
              <GenericPermissionContextProvider>
                <SurveySlideIn />
                <StatusFilterContextProvider>
                  <Snackbar open={isVersionSnackbarOpen}>
                    <VersionAlert />
                  </Snackbar>
                  <Router basename={BASE_PATH}>
                    <Switch>
                      <Route
                        path="(/error-example)"
                        exact
                        render={() => (
                          <ErrorBoundary>
                            <ErrorExamplePage />
                          </ErrorBoundary>
                        )}
                      />
                      <Route
                        path="(/|/requests)"
                        exact
                        render={() => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <SelectedColumnsProvider>
                                <DashboardPage />
                              </SelectedColumnsProvider>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />
                      <Route
                        path="/requests/:testRequestId/:tabName?/:dialog?"
                        render={() => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <TestRequestPermissionContextProvider>
                                <RequestsPage />
                              </TestRequestPermissionContextProvider>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />
                      <Route
                        path="/search"
                        render={() => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <SelectedColumnsProvider>
                                <SearchPage />
                              </SelectedColumnsProvider>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />
                      <Route exact path="/products">
                        <Redirect to="products/prototype" />
                      </Route>
                      <Route
                        path="/products/:productType"
                        render={() => {
                          return (
                            <ErrorBoundary>
                              <CustomVerticalNavbar>
                                <GenericPermissionContext.Consumer>
                                  {(genericPermissionContext) => {
                                    const { accessProducts } = genericPermissionContext;
                                    return accessProducts ? <ProductsPage /> : <AccessDeniedPage />;
                                  }}
                                </GenericPermissionContext.Consumer>
                              </CustomVerticalNavbar>
                            </ErrorBoundary>
                          );
                        }}
                      />
                      <Route exact path="/admin">
                        <Redirect to="admin/statistics" />
                      </Route>
                      <Route
                        path="/admin/labs/:tabIndex?"
                        render={() => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <GenericPermissionContext.Consumer>
                                {({ extendedAdminRights, viewLaboratoryTab }) => {
                                  return extendedAdminRights || viewLaboratoryTab ? (
                                    <LaboratoryPage />
                                  ) : (
                                    <AccessDeniedPage />
                                  );
                                }}
                              </GenericPermissionContext.Consumer>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />
                      <Route
                        path="/admin/report-templates"
                        render={() => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <GenericPermissionContext.Consumer>
                                {({ extendedAdminRights, viewReportTemplatesTab }) => {
                                  return extendedAdminRights || viewReportTemplatesTab ? (
                                    <ReportTemplatePage />
                                  ) : (
                                    <AccessDeniedPage />
                                  );
                                }}
                              </GenericPermissionContext.Consumer>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />

                      <Route
                        path="/admin/:adminSection"
                        render={(props) => (
                          <ErrorBoundary>
                            <CustomVerticalNavbar>
                              <GenericPermissionContext.Consumer>
                                {({ accessAdmin, extendedAdminRights }) => {
                                  const noAdminAccess =
                                    !accessAdmin ||
                                    (props.match.params.adminSection === "settings" && !extendedAdminRights);
                                  return noAdminAccess ? <AccessDeniedPage /> : <AdminPage />;
                                }}
                              </GenericPermissionContext.Consumer>
                            </CustomVerticalNavbar>
                          </ErrorBoundary>
                        )}
                      />
                      <Route path="/auth">
                        <Redirect to="/" />
                      </Route>
                    </Switch>
                  </Router>
                </StatusFilterContextProvider>
              </GenericPermissionContextProvider>
            )}
            {((!loading && !hasViewPermission(user?.roles)) || error?.response?.status === 403) && (
              <AccessDeniedPage noNavbar />
            )}
          </div>
        );
      }}
    </UserContext.Consumer>
  );
}

export default App;
